import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "./icons.jsx";
import { GuruAvatar, StarRow } from "./primitives.jsx";
import ActionButton from "../ActionButton/ActionButton";
import HoverVideoArt from "./HoverVideoArt.jsx";
import {
  PaginationDotsRoot,
  PaginationDot,
  MobileCardRoot,
  MobileCardBody,
  MobileCardGuruRow,
  MobileCardGuruName,
  MobileCardTitle,
  MobileCardRatingRow,
  MobileCardRatingText,
  MobileCardStats,
  MobileCardStatItem,
  MobileCardStatIcon,
  MobileCardStatText,
  MobileCardActions,
} from "./style.js";

// ── PaginationDots ────────────────────────────────────────────────────────────
export const PaginationDots = ({ total, current, max = 5 }) => {
  if (total <= 1) return null;
  const half = Math.floor(max / 2);
  let start = Math.max(0, current - half);
  let end = start + max;
  if (end > total) {
    end = total;
    start = Math.max(0, end - max);
  }
  const visible = Array.from({ length: end - start }, (_, i) => start + i);

  return (
    <PaginationDotsRoot>
      {visible.map((idx) => {
        const isActive = idx === current;
        return <PaginationDot key={idx} $isActive={isActive} />;
      })}
    </PaginationDotsRoot>
  );
};

// ── MobileCard ────────────────────────────────────────────────────────────────
export const MobileCard = ({
  program,
  playToken,
  onAskPlay,
  isVisible,
  onPreview,
  onOpen,
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (isVisible) onAskPlay?.(program.id);
  }, [isVisible, program.id, onAskPlay]);

  return (
    <MobileCardRoot>
      <HoverVideoArt
        program={program}
        hovered={isVisible}
        playToken={isVisible ? program.id : null}
        onAskPlay={onAskPlay}
      />
      <MobileCardBody>
        {/* Guru row */}
        <MobileCardGuruRow>
          <GuruAvatar
            src={program.guru?.photo}
            name={program.guru?.full_name}
            size={30}
            ring={(program.accent ?? ["#01ECFF"])[0]}
          />
          <MobileCardGuruName>{program.guru?.full_name}</MobileCardGuruName>
        </MobileCardGuruRow>
        {/* Title */}
        <MobileCardTitle>{program.name}</MobileCardTitle>
        {/* Rating */}
        <MobileCardRatingRow>
          <StarRow value={program.rating} size={13} />
          <MobileCardRatingText>
            {program.rating} ({program.review_count})
          </MobileCardRatingText>
        </MobileCardRatingRow>
        {/* Stats row */}
        <MobileCardStats>
          {[
            [<Icons.clock size={12} />, program.duration, "Duration"],
            [
              <Icons.book size={12} />,
              `${program.lesson_count} Sessions`,
              "Skill Dev",
            ],
            [
              <Icons.users size={12} />,
              `${program.sim_count} Challenges`,
              "Simulations",
            ],
          ].map(([icon, val, lbl]) => (
            <MobileCardStatItem key={lbl}>
              <MobileCardStatIcon>{icon}</MobileCardStatIcon>
              <MobileCardStatText>
                <strong>{val}</strong>
              </MobileCardStatText>
            </MobileCardStatItem>
          ))}
        </MobileCardStats>
      </MobileCardBody>
      <MobileCardActions>
        {program.enrollment_status ? (
          <ActionButton
            variant="primary"
            size="sm"
            iconRight={<Icons.arrowRight size={13} />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/trainingprogram/${program.trial_program_id ?? program.id}`);
            }}
          >
            {program.enrollment_status === "trial" ? "Continue trial" : "Continue learning"}
          </ActionButton>
        ) : (
          <ActionButton
            variant="primary"
            size="sm"
            icon={<Icons.zap size={13} />}
            onClick={(e) => {
              e.stopPropagation();
              onPreview(program);
            }}
          >
            Start for Free
          </ActionButton>
        )}
        <ActionButton
          variant="purpleOutline"
          size="sm"
          iconRight={<Icons.arrowRight size={13} />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(program);
          }}
        >
          Explore
        </ActionButton>
      </MobileCardActions>
    </MobileCardRoot>
  );
};

// ── MobileCarousel ────────────────────────────────────────────────────────────
const MobileCarousel = ({
  programs,
  playToken,
  onAskPlay,
  onPreview,
  onOpen,
}) => {
  const scrollRef = useRef(null);
  const [currentIdx, setCurrentIdx] = useState(0);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const itemW = el.offsetWidth;
    const idx = Math.round(el.scrollLeft / itemW);
    setCurrentIdx(Math.min(idx, programs.length - 1));
  }, [programs.length]);

  return (
    <div>
      <div ref={scrollRef} className="mp-carousel" onScroll={handleScroll}>
        {programs.map((p, i) => (
          <div key={p.id} className="mp-carousel-item">
            <MobileCard
              program={p}
              playToken={playToken}
              onAskPlay={onAskPlay}
              isVisible={i === currentIdx}
              onPreview={onPreview}
              onOpen={onOpen}
            />
          </div>
        ))}
      </div>
      <PaginationDots total={programs.length} current={currentIdx} max={5} />
    </div>
  );
};

export default MobileCarousel;
